import { useCallback, useState } from 'react';
import { DATE_TYPE, DROPDOWN_TYPE, EXACT_TYPE, RANGE_TYPE, UNKNOWN } from './constants';
import { compileFilters } from './filterUtils';

let nextFilterId = 0;
const createFilter = () => ({
  id: (nextFilterId += 1),
  type: '',
  var: '',
  value1: '',
  value2: '',
  dataType: '',
  label: ''
});

export default function useTableFilters(configuration, commitParams, compile = compileFilters) {
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [draftFilters, setDraftFilters] = useState(() => [createFilter()]);
  const [appliedFilters, setAppliedFilters] = useState([]);
  const [filterError, setFilterError] = useState('');
  const [highlightedFilterId, setHighlightedFilterId] = useState(null);

  const clearFilterState = useCallback(() => {
    setDraftFilters([createFilter()]);
    setAppliedFilters([]);
    setFilterError('');
    setHighlightedFilterId(null);
  }, []);

  const applyFilters = useCallback(() => {
    const result = compile(draftFilters, configuration);
    if (result.errors.length > 0) {
      setFilterError(result.errors[0].message);
      return;
    }

    setAppliedFilters(result.appliedFilters);
    setFilterError('');
    setHighlightedFilterId(null);
    setFiltersOpen(false);
    commitParams(result.params);
  }, [commitParams, compile, configuration, draftFilters]);

  const clearFilters = useCallback(() => {
    clearFilterState();
    commitParams(compile([]).params);
  }, [clearFilterState, commitParams, compile]);

  const resetFilters = useCallback(() => {
    clearFilterState();
    setFiltersOpen(false);
    commitParams(compile([]).params);
  }, [clearFilterState, commitParams, compile]);

  const addNewFilter = useCallback(() => {
    setDraftFilters((current) => [...current, createFilter()]);
  }, []);

  const updateDraftFilters = useCallback((update) => {
    setFilterError('');
    setDraftFilters(update);
  }, []);

  const removeAppliedFilter = useCallback(
    (filterId) => {
      const remainingFilters = appliedFilters.filter((filter) => filter.id !== filterId);
      const result = compile(remainingFilters, configuration);

      setAppliedFilters(result.appliedFilters);
      setDraftFilters((current) => {
        const remainingDrafts = current.filter((filter) => filter.id !== filterId);
        return remainingDrafts.length > 0 ? remainingDrafts : [createFilter()];
      });
      setFilterError('');
      commitParams(result.params);
    },
    [appliedFilters, commitParams, compile, configuration]
  );

  const getFilterLabel = useCallback(
    (filter) => {
      const fieldConfiguration = configuration?.[filter.type]?.[filter.var];
      const fieldLabel = fieldConfiguration?.label || filter.var;
      const valueLabel = fieldConfiguration?.options?.find((option) => option.value === filter.value1)?.label ?? filter.value1;

      if (filter.type === EXACT_TYPE || filter.type === DROPDOWN_TYPE) return `${fieldLabel} = ${valueLabel}`;

      if (filter.type === RANGE_TYPE) {
        const hasMinimum = filter.value1 !== '' && filter.value1 !== null && filter.value1 !== undefined;
        const hasMaximum = filter.value2 !== '' && filter.value2 !== null && filter.value2 !== undefined;
        const formatValue = (value) => (fieldConfiguration?.valType === DATE_TYPE ? new Date(value).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : value);

        if (hasMinimum && hasMaximum) return `${fieldLabel} from ${formatValue(filter.value1)} to ${formatValue(filter.value2)}`;
        return `${fieldLabel} ${hasMinimum ? `from ${formatValue(filter.value1)}` : `to ${formatValue(filter.value2)}`}`;
      }

      return UNKNOWN;
    },
    [configuration]
  );

  const toggleFilters = useCallback(() => {
    setHighlightedFilterId(null);
    setFiltersOpen((current) => !current);
  }, []);
  const closeFilters = useCallback(() => {
    setHighlightedFilterId(null);
    setFiltersOpen(false);
  }, []);
  const openFilter = useCallback((filterId) => {
    setHighlightedFilterId(filterId);
    setFiltersOpen(true);
  }, []);

  return {
    filtersOpen,
    draftFilters,
    appliedFilters,
    filterError,
    highlightedFilterId,
    validFilters: appliedFilters.length,
    toggleFilters,
    closeFilters,
    openFilter,
    updateDraftFilters,
    applyFilters,
    clearFilters,
    resetFilters,
    addNewFilter,
    removeAppliedFilter,
    getFilterLabel
  };
}
